import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, Calendar, Clock, Package, Truck, User, Search, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { getAuthHeaders } from '../../utils/getauthheaders';
import { apiFetch } from '../../utils/apiClient';

const API_BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api`;

const ExpedieriFinalizatePage = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('toate');

  // Fetch completed shipments from API
  const fetchCompletedShipments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch shipments with detailed format
      const response = await fetch(`${API_BASE_URL}/shipments?format=detailed`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Eroare la încărcarea expedierilor');
      }
      
      const data = await response.json();
      
      // Filter only completed shipments and transform data
      const completedShipments = data.data
        ?.filter(shipment => shipment.status === 'livrat')
        ?.map(shipment => ({
          id: shipment.id,
          client: shipment.client_nume || shipment.client || 'Client necunoscut',
          destinatie: shipment.ruta || shipment.destinatie_oras || shipment.destinatie || 'Destinație necunoscută',
          sofer: shipment.sofer_nume || 'Șofer nespecificat',
          telefon: shipment.sofer_telefon || '',
          camion: shipment.vehicul_complet || shipment.vehicul_numar || 'Vehicul nespecificat',
          status: shipment.status,
          oraPlecarii: shipment.ora_plecarii ? new Date(shipment.ora_plecarii).toLocaleTimeString('ro-RO', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }) : '',
          oraEstimata: shipment.ora_estimata ? new Date(shipment.ora_estimata).toLocaleTimeString('ro-RO', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }) : '',
          // Pentru expedierile livrate, folosim ora estimată ca și ora livrării (în lipsa unei ore concrete)
          oraLivrarii: shipment.ora_estimata ? new Date(shipment.ora_estimata).toLocaleTimeString('ro-RO', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }) : '',
          prioritate: shipment.prioritate || 'normala',
          progres: 100,
          dataLivrarii: shipment.data_transport || shipment.created_at ? 
            new Date(shipment.data_transport || shipment.created_at).toLocaleDateString('ro-RO') : '',
          
          // Extract distance from description or set default
          distanta: extractDistance(shipment.descriere) || '0',
          
          // Default rating (poate fi adăugat în baza de date mai târziu)
          rating: 5,
          
          // Additional fields
          descriere: shipment.descriere || '',
          valoare: shipment.valoare || shipment.value || 0,
          tip_plata: shipment.tip_plata || 'cash',
          created_at: shipment.created_at,
          updated_at: shipment.updated_at
        })) || [];
      
      setShipments(completedShipments);
    } catch (error) {
      console.error('Eroare la încărcarea expedierilor finalizate:', error);
      setError('Eroare la încărcarea expedierilor: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract distance from description
  const extractDistance = (descriere) => {
    if (!descriere) return null;
    const distanceMatch = descriere.match(/(\d+(?:\.\d+)?)\s*km/i);
    return distanceMatch ? `${distanceMatch[1]} km` : null;
  };

  // Filter based on search and period
  const filteredShipments = useMemo(() => {
    return shipments.filter(shipment => {
      const matchesSearch = 
        (shipment.client || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (shipment.sofer || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (shipment.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (shipment.destinatie || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Period filtering
      let matchesPeriod = true;
      if (selectedPeriod !== 'toate' && shipment.dataLivrarii) {
        const shipmentDate = new Date(shipment.dataLivrarii);
        const today = new Date();
        
        switch (selectedPeriod) {
          case 'azi':
            matchesPeriod = shipmentDate.toDateString() === today.toDateString();
            break;
          case 'saptamana':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesPeriod = shipmentDate >= weekAgo;
            break;
          case 'luna':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesPeriod = shipmentDate >= monthAgo;
            break;
        }
      }
      
      return matchesSearch && matchesPeriod;
    });
  }, [shipments, searchTerm, selectedPeriod]);

  // Statistics calculations
  const statistics = useMemo(() => {
    const total = filteredShipments.length;
    const onTime = filteredShipments.filter(s => 
      s.oraLivrarii && s.oraEstimata && s.oraLivrarii <= s.oraEstimata
    ).length;
    const averageRating = total > 0 
      ? (filteredShipments.reduce((acc, s) => acc + (s.rating || 5), 0) / total).toFixed(1)
      : '5.0';
    const totalDistance = filteredShipments.reduce((acc, s) => {
      const distance = s.distanta ? parseInt(s.distanta.replace(/\D/g, '')) || 0 : 0;
      return acc + distance;
    }, 0);

    return {
      total,
      onTime,
      averageRating,
      totalDistance
    };
  }, [filteredShipments]);

  useEffect(() => {
    fetchCompletedShipments();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'livrat': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgenta': return 'text-red-600';
      case 'normala': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const getRatingStars = (rating) => {
    return '★'.repeat(rating || 5) + '☆'.repeat(5 - (rating || 5));
  };

  const onRefresh = () => {
    fetchCompletedShipments();
  };

  if (loading) {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-lg text-gray-700 font-medium">Se încarcă expedierile finalizate...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">Eroare la încărcarea datelor</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Încearcă din nou
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Expedieri Finalizate</h1>
            <p className="text-gray-600">Vizualizare și analiză expedieri completate cu succes</p>
          </div>
          <button
            onClick={onRefresh}
            className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Actualizează
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-64 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Căutare după client, șofer sau ID expediere..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select 
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="toate">Toate perioadele</option>
            <option value="azi">Astăzi</option>
            <option value="saptamana">Această săptămână</option>
            <option value="luna">Această lună</option>
          </select>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Finalizate</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
            </div>
          </div>
        </div>
        
       
        
        
        
        
      </div>

      {/* Shipments List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Lista Expedierilor Finalizate</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expediere
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client & Destinație
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Șofer & Vehicul
                </th>
                
               
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredShipments.map((shipment) => (
                <tr key={shipment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">#{shipment.id}</div>
                        <div className="text-sm text-gray-500">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(shipment.status)}`}>
                            Livrat cu succes
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{shipment.client}</div>
                    <div className="text-sm text-gray-500 line-clamp-2">{shipment.destinatie}</div>
                    {shipment.valoare > 0 && (
                      <div className="text-xs text-green-600 font-medium">
                        {typeof shipment.valoare === 'string' ? shipment.valoare : `${shipment.valoare} RON`}
                      </div>
                    )}
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{shipment.sofer}</div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Truck className="h-4 w-4 mr-1" />
                          {shipment.camion}
                        </div>
                        {shipment.telefon && (
                          <div className="text-xs text-blue-600">
                            {shipment.telefon}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  
                  
                  
                  
                  
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredShipments.length === 0 && !loading && (
          <div className="text-center py-12">
            <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nu există expedieri finalizate</h3>
            <p className="mt-1 text-sm text-gray-500">
              {shipments.length === 0 
                ? 'Nu s-au găsit expedieri finalizate în sistem.' 
                : 'Nu s-au găsit expedieri finalizate pentru criteriile selectate.'
              }
            </p>
            {shipments.length > 0 && (
              <button 
                onClick={() => {
                  setSelectedPeriod('toate');
                  setSearchTerm('');
                }}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Resetează filtrele
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpedieriFinalizatePage;